const Z_THRESHOLD_95 = 1.96;
const STABILITY_RANGE_PP = 15; // percentage points

/**
 * U+2014 Em-Dash als Platzhalter fuer "kein Wert".
 *
 * Das ist bewusst KEIN Verstoss gegen die Projektregel "keine
 * Gedankenstriche in der Copy". Die Regel zielt auf Prosa -- ein Strich
 * mitten im Satz liest sich maschinell erzeugt. Ein allein stehender Strich
 * in einer Tabellenzelle ist dagegen ein typografisches Zeichen fuer
 * "kein Wert", keine Formulierung. Der Guard in
 * `src/locales/noTypographicDashes.test.ts` laesst deshalb genau diesen
 * Fall zu (allein stehend), und nur diesen.
 */
const NA_DASH = '—';

const SIGNIFICANCE_LABELS = {
  de: { significant: 'signifikant', noise: 'rauschen', insufficient: 'zu wenig Daten' },
  en: { significant: 'significant', noise: 'noise', insufficient: 'insufficient' },
} as const;

/**
 * Trennwort fuer Spannen.
 *
 * PR #92 hat diese Entscheidung fuer die identische Konstruktion schon
 * getroffen: die Datumsspanne im Upload-Banner ging von
 * `{{leftDate}} – {{rightDate}}` auf `{{leftDate}} bis {{rightDate}}`.
 * Bemerkenswert daran ist, dass dort nicht auf einen Bindestrich
 * ausgewichen wurde, sondern auf das ausgeschriebene Wort -- "einfach einen
 * Bindestrich nehmen" ist als Mittelweg also bereits verworfen.
 *
 * Typografisch waere der Halbgeviertstrich in einer Spanne korrekt. Hier
 * schlaegt Konsistenz mit der harten Projektregel die Typografie: eine
 * Oberflaeche, die Zeitraeume mit "bis" und Prozentbaender mit "–" schreibt,
 * ist in sich widerspruechlich, und der Strich war ohnehin nur deshalb
 * stehen geblieben, weil er in einem `.ts`-Literal statt in einem
 * Locale-JSON steht und der Guard dort nicht hinreichte.
 */
const RANGE_SEPARATORS = { de: 'bis', en: 'to' } as const;

export type StatsLocale = keyof typeof SIGNIFICANCE_LABELS;

export function formatCtrCI(
  lower: number | null,
  upper: number | null,
  locale: StatsLocale = 'en',
): string {
  if (lower === null || upper === null) return NA_DASH;
  if (!Number.isFinite(lower) || !Number.isFinite(upper)) return NA_DASH;
  const separator = RANGE_SEPARATORS[locale] ?? RANGE_SEPARATORS.en;
  return `${(lower * 100).toFixed(2)}% ${separator} ${(upper * 100).toFixed(2)}%`;
}

export function formatZScore(z: number | null): string {
  if (z === null || !Number.isFinite(z)) return NA_DASH;
  const sign = z >= 0 ? '+' : '';
  return `${sign}${z.toFixed(2)}`;
}

export type SignificanceState = 'significant' | 'noise' | 'insufficient';

export function getSignificanceState(z: number | null): SignificanceState {
  if (z === null) return 'insufficient';
  return Math.abs(z) >= Z_THRESHOLD_95 ? 'significant' : 'noise';
}

export function significanceLabel(z: number | null, locale: 'de' | 'en'): string {
  return SIGNIFICANCE_LABELS[locale][getSignificanceState(z)];
}

/** Locale label for an explicit state (used when the badge derives state from gates, not z alone). */
export function stateLabel(state: SignificanceState, locale: 'de' | 'en'): string {
  return SIGNIFICANCE_LABELS[locale][state];
}

/**
 * Single source of truth for the data-quality gates. Mirrors the SQL `is_significant`
 * gate (without the |z|>=1.96 part). Used by the dashboard's `trustworthyProducts`
 * filter and the badge's `gatesPass` check to prevent drift.
 */
export interface DataGateInputs {
  pre_impressions: number;
  post_impressions: number;
  pre_clicks: number;
  post_clicks: number;
  effective_window_days: number;
  pre_days_with_data: number;
  post_days_with_data: number;
}

export function passesDataGates(row: DataGateInputs): boolean {
  if (row.effective_window_days < 7) return false;
  if (row.pre_impressions < 100 || row.post_impressions < 100) return false;
  if (row.pre_clicks < 5 || row.post_clicks < 5) return false;
  const minDays = Math.ceil(row.effective_window_days * 0.7);
  if (row.pre_days_with_data < minDays || row.post_days_with_data < minDays) return false;
  return true;
}

export function formatMde(mdePct: number | null): string {
  if (mdePct === null) return NA_DASH;
  return `±${Math.round(mdePct)}%`;
}

export function medianOf(values: Array<number | null>): number | null {
  const filtered = values.filter((v): v is number => v !== null && !Number.isNaN(v));
  if (filtered.length === 0) return null;
  const sorted = [...filtered].sort((a, b) => a - b);
  const n = sorted.length;
  return n % 2 === 0 ? (sorted[n / 2 - 1] + sorted[n / 2]) / 2 : sorted[Math.floor(n / 2)];
}

export type StabilityState = 'stable' | 'volatile' | 'incomplete';

export function stabilityState(
  d7: number | null,
  d14: number | null,
  d28: number | null,
): StabilityState {
  const deltas = [d7, d14, d28].filter((v): v is number => v !== null);
  if (deltas.length < 2) return 'incomplete';
  const range = Math.max(...deltas) - Math.min(...deltas);
  return range <= STABILITY_RANGE_PP ? 'stable' : 'volatile';
}

/**
 * Pooled aggregate CTR-lift over the given rows.
 *
 *   lift = (Σpost_clk / Σpost_imp − Σpre_clk / Σpre_imp) / (Σpre_clk / Σpre_imp) × 100
 *
 * This is the "click-weighted" lift — the change in overall CTR if you treat
 * all rows as one population. Differs from a simple mean of individual lifts,
 * which would treat a low-traffic product with a +200% lift the same as a
 * high-traffic product with a +5% lift. The pooled formula reflects the real
 * aggregate effect on the click stream.
 *
 * Returns null when pre clicks or pre impressions sum to zero (lift undefined).
 */
export interface ClickWeightedInputs {
  pre_clicks: number;
  pre_impressions: number;
  post_clicks: number;
  post_impressions: number;
}

export function clickWeightedLift(rows: ClickWeightedInputs[]): number | null {
  if (rows.length === 0) return null;
  let preClk = 0, preImp = 0, postClk = 0, postImp = 0;
  for (const r of rows) {
    preClk += r.pre_clicks;
    preImp += r.pre_impressions;
    postClk += r.post_clicks;
    postImp += r.post_impressions;
  }
  if (preImp === 0 || postImp === 0 || preClk === 0) return null;
  const preCtr = preClk / preImp;
  const postCtr = postClk / postImp;
  return ((postCtr - preCtr) / preCtr) * 100;
}
